import { ChangeDetectionStrategy, Component } from '@angular/core';
import { IconV3Component } from '@gea/digital-ui-lib';
import { FieldWrapper, FormlyModule } from '@ngx-formly/core';

/**
 * Renders a colored "group card" (background + icon + label), used e.g. for the
 * Condensation / Evaporation groups on the Conditions tab.
 *
 * Fully data-driven from layout.json — nothing here is tied to a specific field
 * group's label or limited to a fixed set of colors/icons:
 *  - `props.background`  — any CSS color/gradient for the card background.
 *  - `props.iconColor`   — any CSS color for the icon.
 *  - `props.icon`        — a gea-icon-v3 file name (e.g. `16px_drop`) to render as the icon.
 *  - `props.variant`     — optional shorthand that looks up a preset in
 *    {@link PRESETS} below (purely a convenience registry which can hold any
 *    number of entries); `background`/`iconColor`/`icon` always take
 *    precedence over the preset when supplied, so any new color or icon can be
 *    used ad-hoc from layout.json without touching this component.
 */
interface ColoredPanelPreset {
  background: string;
  iconColor: string;
  icon?: string;
}

/** Convenience presets — extend freely, this registry is not capped at two entries. */
const PRESETS: Record<string, ColoredPanelPreset> = {
  heatSink: {
    background: 'rgba(255, 200, 203, 0.3)',
    iconColor: 'var(--geaui-tertiary-red-70, #db1e28)',
    icon: '16px_drop',
  },
  heatSource: {
    background: 'rgba(191, 227, 255, 0.4)',
    iconColor: 'var(--geaui-primary, #1f9dff)',
    icon: '16px_snow',
  },
};

const DEFAULT_PRESET: ColoredPanelPreset = { background: 'transparent', iconColor: 'inherit' };

@Component({
  selector: 'formly-colored-panel-wrapper',
  imports: [FormlyModule, IconV3Component],
  template: `
    <div class="colored-panel" [style.background]="background">
      @if (props.label) {
        <div class="colored-panel-heading">
          @if (icon) {
            <geaui-icon-v3 [file]="icon" [color]="iconColor"></geaui-icon-v3>
          }
          <span class="subtitle-14">{{ props.label }}</span>
        </div>
      }
      <div class="colored-panel-body">
        <ng-container #fieldComponent></ng-container>
      </div>
    </div>
  `,
  styles: [
    `
      .colored-panel {
        border-radius: 0.75rem;
        padding: 1rem;
      }
      .colored-panel-heading {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0 1rem 0;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColoredPanelWrapperComponent extends FieldWrapper {
  private get preset(): ColoredPanelPreset {
    const variant = this.props?.['variant'] as string | undefined;
    return (variant && PRESETS[variant]) || DEFAULT_PRESET;
  }

  /** Any CSS color/gradient — explicit `props.background` wins over the preset. */
  get background(): string {
    return (this.props?.['background'] as string | undefined) ?? this.preset.background;
  }

  /** Any CSS color — explicit `props.iconColor` wins over the preset. */
  get iconColor(): string {
    return (this.props?.['iconColor'] as string | undefined) ?? this.preset.iconColor;
  }

  /** gea-icon-v3 file name (e.g. `16px_drop`) — explicit `props.icon` wins over the preset's icon. */
  get icon(): string | undefined {
    return (this.props?.['icon'] as string | undefined) ?? this.preset.icon;
  }
}
